/* eslint react/no-children-prop: 0 */
import React from 'react'
import Responsive from 'react-responsive'
import { Text, Link } from 'rebass'
import { rem, lighten, borderRadius } from 'polished'
import { Box } from 'rebass'
import styled, { css } from 'styled-components'
import theme from '../theme/'
import IconTick from '../vectors/icon-tick.svg'
import PropTypes from 'prop-types'
import { space, layout, color } from 'styled-system'

export const sizes = {
  GiantScreenUp: theme.mediaBreakpoints[2],
  LargeScreenUp: theme.mediaBreakpoints[1],
  MediumScreenUp: theme.mediaBreakpoints[0],
  SmallishScreenUp: 550
}

export const lazyload = {
  offset: 300
}

export const GiantScreenUp = ({ children }) => (
  <Responsive minWidth={1170} children={children} />
)
export const LargeScreenUp = ({ children }) => (
  <Responsive minWidth={993} children={children} />
)
export const MediumScreenUp = ({ children }) => (
  <Responsive minWidth={768} children={children} />
)
export const MediumScreenOnly = ({ children }) => (
  <Responsive minWidth={768} maxWidth={992} children={children} />
)
export const SmallScreenOnly = ({ children }) => (
  <Responsive maxWidth={767} children={children} />
)
export const SmallToMediumScreenOnly = ({ children }) => (
  <Responsive maxWidth={992} children={children} />
)
export const SmallToGiantScreenOnly = ({ children }) => (
  <Responsive maxWidth={1169} children={children} />
)

// iterate through the sizes and create a media template
export const media = Object.keys(sizes).reduce((accumulator, label) => {
  // use em in breakpoints to work properly cross-browser and support users
  // changing their browsers font-size: https://zellwk.com/blog/media-query-units/
  const emSize = sizes[label] / 16
  accumulator[label] = (...args) => css`
    @media (min-width: ${emSize}em) {
      ${css(...args)};
    }
  `
  return accumulator
}, {})

const ContainerBase = styled.div`
  margin: 0 auto;
  ${space}
  ${layout}
  ${color}
`

export const Container = (props) => <ContainerBase px={3} {...props} />

export const Grid = styled(Box)`
  display: inline-box;
  vertical-align: top;
`

export const Blockquote = (props) => (
  <Text as="blockquote" m={0} fontSize={3} {...props} />
)

export const Small = (props) => (
  <Text as="small" m={0} fontSize={0} {...props} />
)

export const Absolute = styled.div`
  position: absolute;
  top: ${(props) => (props.top ? 0 : null)};
  bottom: ${(props) => (props.bottom ? 0 : null)};
  left: ${(props) => (props.left ? 0 : null)};
  right: ${(props) => (props.right ? 0 : null)};
  z-index: ${(props) => (props.zIndex ? props.zIndex : null)};

  ${space}
  ${layout}
  ${color}
`

Absolute.propTypes = {
  top: PropTypes.bool,
  bottom: PropTypes.bool,
  left: PropTypes.bool,
  right: PropTypes.bool,
  zIndex: PropTypes.number
}

export const Relative = styled.div`
  position: relative;
  z-index: ${(props) => (props.zIndex ? props.zIndex : null)};

  ${space}
  ${layout}
  ${color}
`

Relative.propTypes = {
  zIndex: PropTypes.number
}

export const Sticky = styled.div`
  position: sticky;
  top: ${(props) => (props.top ? 0 : null)};
  bottom: ${(props) => (props.bottom ? 0 : null)};
  left: ${(props) => (props.left ? 0 : null)};
  right: ${(props) => (props.right ? 0 : null)};
  z-index: ${(props) => (props.zIndex ? props.zIndex : null)};

  ${space}
  ${layout}
  ${color}
`

Sticky.propTypes = {
  top: PropTypes.bool,
  bottom: PropTypes.bool,
  left: PropTypes.bool,
  right: PropTypes.bool,
  zIndex: PropTypes.number
}

export const NavLink = styled((props) => <Link variant="nav" {...props} />)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

export const Panel = styled.div`
  overflow: hidden;
  border-radius: ${(props) => props.theme.radius}px;
  border-width: 1px;
  border-style: solid;

  ${space}
  ${layout}
  ${color}
`
export const PanelHeader = styled.header`
  border-bottom-width: 1px;
  border-bottom-style: solid;

  ${space}
  ${layout}
  ${color}
`
export const PanelFooter = styled.footer`
  border-top-width: 1px;
  border-top-style: solid;

  ${space}
  ${layout}
  ${color}
`

export const Truncate = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const BadgeBase = styled.div`
  font-weight: bold;
  display: inline-block;
  vertical-align: middle;
  border-radius: ${(props) => props.theme.radius}px;

  ${space}
  ${layout}
  ${color}
`

export const Badge = (props) => (
  <BadgeBase
    {...{
      fontSize: 0,
      p: 1,
      ml: 1,
      mr: 1,
      color: 'white',
      bg: 'blue'
    }}
    {...props}
  />
)

export const VisuallyHidden = `
    border: 0;
    clip: rect(0 0 0 0);
    -webkit-clip-path: inset(50%);
    clip-path: inset(50%);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
    white-space: nowrap; /* 1 */`

export const InlineStyleResetProperties = [
  'box-sizing',
  'color',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'line-height',
  'margin',
  'padding',
  'text-decoration',
  'white-space'
]

export const InlineStyleReset = `
  ${InlineStyleResetProperties.map(
    (property) => `
    [style*="${property}"] {
      ${property}: inherit !important;
    }
  `
  )}
`

export const ImageNavLink = `
  position: relative;
  width: 50px;
  img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`

export const CategoryBadge = styled(Badge)`
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  line-height: 1.1;
  z-index: 1;
  top: 20px;
  left: 25px;
  padding: 4px 10px;
  display: inline-block;

  margin-left: 0;
  margin-right: 4px;
  margin-bottom: 4px;
`

export const RecentBadge = styled(CategoryBadge)`
  display: inline-block;
  background-color: ${(props) => props.theme.colors.grey};
`
RecentBadge.displayName = 'RecentBadge'

export const ReservedBadge = styled(CategoryBadge)`
  display: inline-block;
`
ReservedBadge.displayName = 'ReservedBadge'

/* Forms */

export const Form = styled.form`
  legend {
    color: ${(props) => props.theme.colors.blue};
    font-weight: ${(props) => props.theme.weights[1]};
    font-size: ${(props) => rem(props.theme.fontSizes[3])};
  }

  fieldset {
    border: 0;
  }

  input,
  .pseudo_input {
    font-size: ${(props) => rem(props.theme.fontSizes[2])};
    color: ${(props) => props.theme.colors.grey};
    border: 2px ${(props) => props.theme.colors.grey_light_op30} solid;
    padding: 1px 15px;
    font-weight: ${(props) => props.theme.weights[0]};

    &::placeholder {
      color: ${(props) => props.theme.colors.blue};
    }
  }

  label {
    cursor: pointer;
    display: inline-block;
    vertical-align: top;
    padding-left: ${rem(5)};
    text-transform: uppercase;
    color: ${(props) => props.theme.colors.grey_light};
    font-size: ${(props) => rem(props.theme.fontSizes[2])};
    font-weight: ${(props) => props.theme.weights[1]};
    line-height: 1.2;
    padding-top: ${rem(10)};
    padding-bottom: ${rem(10)};
    max-width: 100%;
  }
`

export const RadioWrapper = styled.div`
  position: relative;
  text-align: left;

  input[type='radio'] {
    visibility: hidden;
    position: absolute;
  }

  input[type='radio'] + label {
    position: relative;
    padding-left: 26px;
    padding-right: 4px;
  }

  input[type='radio'] + label::before {
    content: '';
    position: absolute;
    display: block;
    height: 20px;
    width: 20px;
    top: ${rem(9)};
    left: 0;
    border-radius: 50%;
    border: 2px ${(props) => props.theme.colors.grey_light_op30} solid;
    background: white;
    transition: border 0.1s ${(props) => props.theme.ease.ease_out_expo};
  }

  input[type='radio'] + label:hover::before,
  input[type='radio']:focus + label::before {
    border: 2px ${(props) => props.theme.colors.blue} solid;
    color: ${(props) => props.theme.colors.blue};
    background: white;
  }

  input[type='radio']:focus + label::before {
    color: ${(props) => props.theme.colors.grey_light};
  }

  input[type='radio']:focus + label {
    color: ${(props) => props.theme.colors.grey_op65};
  }

  input[type='radio'] + label {
    transition: color 0.3s ${(props) => props.theme.ease.ease_out_expo};
  }

  input[type='radio'] + label:hover {
    color: ${(props) => props.theme.colors.blue};
  }

  input[type='radio'] + label::after {
    content: '';
    position: absolute;
    display: block;
    width: ${rem(8)};
    height: ${rem(8)};
    top: ${rem(9)};
    left: 0;
    border-radius: 50%;
    background: ${(props) => props.theme.colors.white};
    opacity: 0;
    transform: scale(0.2);
    transition: transform 0.8s ${(props) => props.theme.ease.ease_out_expo},
      opacity 0.2s ${(props) => props.theme.ease.ease_out_expo};
  }

  input[type='radio']:checked + label::after {
    opacity: 1;
    transform: scale(1);
  }

  input[type='radio']:checked + label::before {
    background: ${(props) => props.theme.colors.blue};
  }

  input[type='radio']:checked + label {
    color: ${(props) => props.theme.colors.blue};
  }
`

export const RadioGroupWrapper = styled.div`
  position: relative;
  width: 50%;

  input[type='radio'] {
    float: left;
    position: absolute;
    top: 50%;
    left: 50%;
    margin: 0;
    transform: translate(-50%, -50%);
    visibility: hidden;
  }

  input[type='radio'] + label {
    z-index: 1;
    position: relative;
    width: 100%;
    text-align: center;
    font-size: ${(props) => rem(props.theme.fontSizes[0])};
    transition: color 0.3s ${(props) => props.theme.ease.ease_out_expo};
    line-height: 3.3;
    margin: 0;
    padding-top: 0;
    padding-bottom: 0;
  }

  input[type='radio'] + label::before {
    content: '';
    position: absolute;
    display: block;
    height: 20px;
    width: 100%;
    top: 0;
    left: 0;
    padding: ${rem(18)} ${rem(10)};
    z-index: -1;
    border: 2px ${(props) => props.theme.colors.grey_light_op30} solid;
    background: white;
    transition: border 0.1s ${(props) => props.theme.ease.ease_out_expo};
  }

  &:first-child input[type='radio'] + label::before {
    ${borderRadius('right', '0')}
    ${borderRadius('left', '4px')}
    border-right: 0;
  }

  &:last-child input[type='radio'] + label::before {
    ${borderRadius('left', '0')}
    ${borderRadius('right', '4px')}
    border-left: 0;
  }

  input[type='radio'] + label:hover::before,
  input[type='radio']:focus + label::before {
    border: 2px ${(props) => props.theme.colors.blue} solid;
    color: ${(props) => props.theme.colors.blue};
    background: white;
  }

  input[type='radio']:focus + label::before {
    color: ${(props) => props.theme.colors.grey_light};
  }

  input[type='radio']:focus + label {
    color: ${(props) => props.theme.colors.grey_op65};
  }

  input[type='radio'] + label:hover {
    color: ${(props) => props.theme.colors.blue};
  }

  input[type='radio']:checked + label::after {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }

  input[type='radio']:checked + label::before {
    background: ${(props) => props.theme.colors.blue};
  }

  input[type='radio']:checked + label {
    color: ${(props) => props.theme.colors.white};
  }
`

export const CheckboxWrapper = styled.div`
  position: relative;
  max-width: 100%;

  input[type='checkbox'] {
    position: absolute;
    visibility: hidden;
  }

  input[type='checkbox'] + label::before {
    content: '';
    position: absolute;
    display: block;
    height: 20px;
    width: 20px;
    top: ${rem(9)};
    left: 0;
    border-radius: ${rem(4)};
    border: 2px ${(props) => props.theme.colors.grey_light_op30} solid;
    background: white;
    transition: border 0.1s ${(props) => props.theme.ease.ease_out_expo};
  }

  input[type='checkbox'] + label:hover::before,
  input[type='checkbox']:focus + label::before {
    border: 2px
      ${(props) =>
        props.disabled
          ? props.theme.colors.grey_light
          : props.theme.colors.blue}
      solid;
    color: ${(props) =>
      props.disabled ? props.theme.colors.grey_light : props.theme.colors.blue};
    background: white;
  }

  input[type='checkbox']:focus + label::before {
    color: ${(props) => props.theme.colors.grey_light};
  }

  input[type='checkbox']:focus + label {
    color: ${(props) => props.theme.colors.grey_op65};
  }

  input[type='checkbox'] + label {
    position: relative;
    padding-left: 26px;
    padding-right: 4px;
    transition: color 0.3s ${(props) => props.theme.ease.ease_out_expo};
  }

  input[type='checkbox'] + label:hover {
    color: ${(props) => props.theme.colors.blue};
  }

  input[type='checkbox'] + label::after {
    content: '';
    position: absolute;
    display: block;
    width: 20px;
    height: 20px;
    top: ${rem(9)};
    left: 0;
    border-radius: ${rem(4)};
    background: ${(props) => props.theme.colors.blue} url(${IconTick}) no-repeat
      center;
    opacity: 0;
    transition: opacity 0.3s ${(props) => props.theme.ease.ease_out_expo};
  }

  input[type='checkbox']:disabled + label {
    color: ${(props) => props.theme.colors.grey_op10};
  }

  input[type='checkbox']:checked + label::after {
    opacity: 1;
  }

  input[type='checkbox']:checked + label {
    color: ${(props) => props.theme.colors.blue};
  }

  input[type='checkbox'] + label:hover::after {
    background: ${(props) => lighten(0.1, props.theme.colors.blue)}
      url(${IconTick}) no-repeat center;
  }
`

export const RheostatSlider = styled(Box)`
  .rheostat {
    overflow: visible;
  }

  .rheostat-background {
    background-color: ${(props) => props.theme.colors.grey_light_op30};
    height: 5px;
    border-radius: 5px;
    position: relative;
  }

  .rheostat-progress {
    ${(props) =>
      !props.disabled && `background-color: ${props.theme.colors.blue};`}
    position: absolute;
    top: 0;
    height: 5px;
  }

  .rheostat-handle {
    background-color: #fff;
    ${(props) => !props.disabled && `cursor: pointer;`}
    border: 1px solid ${(props) => props.theme.colors.grey_light_op30};
    border-radius: 50%;
    width: 30px;
    height: 30px;
    ${media.LargeScreenUp`
      width: 20px;
      height: 20px;
    `};
    padding: 0;
    outline: none;
    transform: translate(-50%, -65%);
    z-index: 2;
    box-shadow: 2px 4px 6px 0 rgba(66, 76, 94, 0.32);
  }

  .rheostat-handle::before,
  .rheostat-handle::after {
    content: '';
    display: block;
    position: absolute;
    background-color: #dadfe8;
  }
`

export const TagsInputWrapper = styled.div`
  .react-tagsinput-tag {
    background-color: ${(props) => props.theme.colors.blue};
    transition: background-color 0.3s
      ${(props) => props.theme.ease.ease_out_expo};
    border-radius: 2px;
    color: white;
    display: inline-block;
    line-height: 1;
    font-size: 13px;
    font-weight: 400;
    margin-right: 5px;
    padding: 5px 8px;

    &:first-child {
      margin-top: 10px;
    }

    &:hover {
      background-color: ${(props) => props.theme.colors.grey};
    }
  }

  .react-tagsinput-remove {
    cursor: pointer;
    font-weight: 600;
    text-decoration: none;
    color: white;
  }

  .react-tagsinput-tag a::before {
    content: ' ×';
    position: relative;
    top: -1px;
    left: 1px;
  }

  .react-tagsinput {
    font-size: ${(props) => rem(props.theme.fontSizes[2])};
    font-weight: ${(props) => props.theme.weights[0]};
    color: ${(props) => props.theme.colors.grey};
    border: 1px ${(props) => props.theme.colors.blue} solid;
    ${borderRadius('right', '5px')};
    ${borderRadius('left', '5px')};
    padding: 0 8px;
    line-height: 2.1;

    &:placeholder {
      color: ${(props) => props.theme.colors.blue};
    }
  }

  .react-tagsinput-input {
    background: transparent;
    border: 0;
    height: 46px;
    padding: 8px 3px;
    outline: none;
    width: 100%;
    margin-top: 0;
  }

  .react-tagsinput-tag + .react-tagsinput-input {
    margin-top: 10px;
    border-top: 1px ${(props) => props.theme.colors.grey_light_op30} solid;
  }
`

export const ReactResponsiveSelectDarkStyle = styled.div`
  .rrs {
    position: relative;
    box-sizing: border-box;
  }

  .rrs *,
  .rrs *::before,
  .rrs *::after {
    box-sizing: border-box;
  }

  .rrs__button {
    font-size: ${(props) => rem(props.theme.fontSizes[1])};
    font-weight: ${(props) => props.theme.weights[1]};
    color: ${(props) => props.theme.colors.white};
    position: relative;
    cursor: pointer;
    height: 40px;
    line-height: 40px;
    border-radius: 2px;
    min-width: 150px;
    text-align: center;
    z-index: 11;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }

  .rrs__button--disabled {
    color: #999;
    background: #f5f5f5;
    cursor: default;
  }

  .rrs__button:focus {
    outline: 0;
  }

  .rrs__button + .rrs__options {
    list-style: none;
    padding: 0;
    margin: 0;
    background: ${(props) => props.theme.colors.grey};
    position: absolute;
    z-index: 2;
    border-radius: 0 0 5px 5px;
    top: 43px;
    width: 100%;
    min-width: 130px;
    height: 0;
    visibility: hidden;
    overflow: hidden;
    white-space: nowrap;
  }

  .rrs--options-visible .rrs__options {
    height: auto;
    visibility: visible !important;
    z-index: 11;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    max-height: 230px;
    box-shadow: 2px 3px 15px 0 rgba(35, 41, 51, 0.6),
      1px 1px 2px 0 rgba(35, 41, 51, 0.6);
    background: ${(props) => props.theme.colors.grey};
  }

  .rrs__option {
    cursor: pointer;
    font-size: ${(props) => rem(props.theme.fontSizes[1])};
    font-weight: ${(props) => props.theme.weights[1]};
    padding: 0.3rem 2rem 0.3rem 1rem;
    margin: 0;
    border-top: rgba(255, 255, 255, 0.1) 1px solid;
    transition: background-color 0.3s
      ${(props) => props.theme.ease.ease_out_expo};
  }

  .rrs__option * {
    pointer-events: none;
  }

  .rrs__option:first-of-type {
    border-top: 0;
  }

  .rrs__option:focus {
    outline: 0;
  }

  .rrs__option:hover {
    background-color: ${(props) => lighten(0.05, props.theme.colors.grey)};
    color: ${(props) => props.theme.colors.white};
  }

  .rrs__option:active {
    background: ${(props) => props.theme.colors.white};
  }

  .rrs__option.rrs__option--next-selection {
    color: ${(props) => props.theme.colors.white};
  }

  .rrs__option.rrs__option--selected {
    color: ${(props) => props.theme.colors.white};
  }

  .rrs__label {
    padding: 0 2.8rem 0 1rem;
    display: inline-flex;
    width: 100%;
    max-width: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    font-size: inherit;
    background: transparent;
    border: 1px solid rgba(0, 0, 0, 0);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .rrs--options-visible .rrs__label,
  .rrs__button:focus .rrs__label {
    outline: 0;
  }

  .rrs__label {
    font-size: inherit;
    background: transparent;
    border: 1px solid rgba(0, 0, 0, 0);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .rrs--has-changed .rrs__label {
    color: ${(props) => props.theme.colors.white};
  }

  /*

    Multiselect overrides

  */

  .rrs__multiselect-label {
    display: inline-flex;
    max-width: 100%;
    line-height: 1;
  }

  .rrs__multiselect-label__text {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .rrs__multiselect-label__badge {
    border: 1px solid #ccc;
    padding: 1px 6px;
    margin: 0 0 0 4px;
    border-radius: 8px;
    color: #666;
    font-size: 11px;
    vertical-align: middle;
    display: inline-block;
  }

  .caret-icon {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
  }
`

export default media
